import React, { useCallback, useMemo, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { Button } from "@/components/ui/button";

type Props = {
  open: boolean;
  src: string;          // image URL (object URL or data URL)
  aspect?: number;      // default 3/4 for MTG cards
  onCancel: () => void;
  onApply: (blob: Blob) => void; // returns a cropped Blob (JPEG)
};

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Object URLs don't need crossOrigin, but leave it safe for remote if ever used.
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = src;
  });
}

async function getCroppedBlob(imageSrc: string, crop: Area, quality = 0.92): Promise<Blob> {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(crop.width));
  canvas.height = Math.max(1, Math.round(crop.height));
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas not supported");

  // Draw the selected crop
  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Crop failed"))),
      "image/jpeg",
      quality,
    );
  });
}

export default function ImageCropDialog({ open, src, aspect = 3 / 4, onCancel, onApply }: Props) {
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);

  const onComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const canApply = useMemo(() => !!src && !!croppedAreaPixels && !busy, [src, croppedAreaPixels, busy]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] grid place-items-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Crop Image"
    >
      <div className="absolute inset-0 bg-black/70" onClick={onCancel} />
      <div
        className="
          relative z-10 w-full max-w-[min(820px,96vw)]
          max-h-[calc(100vh-2rem)]
          rounded-2xl border border-white/10 bg-gray-900/95 backdrop-blur
          shadow-2xl overflow-hidden flex flex-col
        "
      >
        <header className="px-4 py-3 border-b border-white/10 flex items-center justify-between shrink-0">
          <div className="text-white font-semibold">Crop image</div>
          <Button variant="outline" size="sm" onClick={onCancel}>Close</Button>
        </header>

        <div className="flex-1 relative bg-black">
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onComplete}
            restrictPosition={false}
            objectFit="contain"
          />
        </div>

        <div className="px-4 sm:px-5 py-3 border-t border-white/10 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-white/80">Zoom</label>
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!croppedAreaPixels) return;
                try {
                  setBusy(true);
                  const blob = await getCroppedBlob(src, croppedAreaPixels);
                  onApply(blob);
                } finally {
                  setBusy(false);
                }
              }}
              disabled={!canApply}
            >
              {busy ? "Cropping…" : "Apply crop"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
